import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { catchError, filter, interval, of, switchMap } from 'rxjs';
import { AppVersion } from '../../models/app-version.model';
import { SKIP_LOADER } from '../interceptors/loader.interceptor';


@Injectable({
  providedIn: 'root'
})
export class VersionCheckService {
  private http = inject(HttpClient);

  private appVersionKey = 'appVersion';
  private checkInterval = 1000 * 60 * 10; // 10 minutes

  updateAvailable = signal<AppVersion | null>(null);

  constructor() {
    this.initVersionCheck();
  }

  private initVersionCheck() {
    // Initial check
    this.checkVersion();

    // Interval check
    interval(this.checkInterval)
      .pipe(
        switchMap(() => this.checkVersionData()),
        catchError(err => {
          console.error('Error checking version:', err);
          return of(null);
        }),
        filter(data => !!data)
      )
      .subscribe(data => {
        if (data && this.isUpdateNeeded(data.version)) {
          this.updateAvailable.set(data);
        }
      });
  }

  private checkVersion() {
    this.checkVersionData().subscribe(data => {
      if (!data) {
        return;
      }

      // First run or cleared storage
      if (!localStorage.getItem(this.appVersionKey)) {
        localStorage.setItem(this.appVersionKey, data.version);
        return;
      }

      if (this.isUpdateNeeded(data.version)) {
        this.updateAvailable.set(data);
      }
    });
  }

  private checkVersionData() {
    // Add a timestamp to bypass cache
    const url = `/version.json?t=${new Date().getTime()}`;
    return this.http.get<AppVersion>(url, {
      context: new HttpContext().set(SKIP_LOADER, true)
    }).pipe(
      catchError(() => of(null))
    );
  }

  private isUpdateNeeded(serverVersion: string): boolean {
    const storedVersion = localStorage.getItem(this.appVersionKey);
    return !storedVersion || storedVersion !== serverVersion;
  }

  commitUpdate() {
    const data = this.updateAvailable();
    if (data) {
      localStorage.setItem(this.appVersionKey, data.version);
    }
  }
}
